import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import { User } from '@/types';

// Fetch all users (minimal data only)
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const data = await apiClient.get<User[]>('/users?minimal=true');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 10 * 60 * 1000, // Background refresh every 10 minutes
    refetchIntervalInBackground: true,
  });
};

// Fetch basic user information (fast - no heavy operations)
export const useUserBasicInfo = (userId: string | null) => {
  return useQuery({
    queryKey: ['user', userId, 'basic'],
    queryFn: async () => {
      if (!userId) return null;
      return await apiClient.get<any>(`/users/${userId}/basic`);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - basic info doesn't change often
  });
};

// Fetch user SSN separately (heavy operation)
export const useUserSSN = (userId: string | null) => {
  return useQuery({
    queryKey: ['user', userId, 'ssn'],
    queryFn: async () => {
      if (!userId) return null;
      return await apiClient.get<{user_id: string, decrypted_ssn: string | null}>(`/users/${userId}/ssn`);
    },
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes - SSN rarely changes
  });
};

// Fetch user images separately (heavy operation)
export const useUserImages = (userId: string | null) => {
  return useQuery({
    queryKey: ['user', userId, 'images'],
    queryFn: async () => {
      if (!userId) return null;
      return await apiClient.get<any>(`/users/${userId}/images`);
    },
    enabled: !!userId,
    staleTime: 15 * 60 * 1000, // 15 minutes - images rarely change
  });
};

// Update user
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { id: string; user: Partial<User> }) => {
      return await apiClient.put(`/users/${data.id}`, data.user);
    },
    onSuccess: (_, variables) => {
      // Invalidate both the users list and the specific user
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
    },
  });
};

// Delete user
export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

// Create user
export const useCreateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (user: Partial<User>) => {
      return await apiClient.post('/users', user);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

};
